import React, { Children, cloneElement, Component } from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import isEqual from 'react-fast-compare';
import reduce from 'lodash/reduce';
import styles from './SideScroller.module.scss';

type Props = {
    children: React.ReactNode | any[];
    className?: string;
    marginBetweenCards: number;
};

type State = {
    left: number;
    nextActive: boolean;
    previousActive: boolean;
    width: number;
};

export default class SideScroller extends Component<Props, State> {
    static defaultProps = {
        marginBetweenCards: 20,
    };

    constructor(props: Props) {
        super(props);
        this.handleScroll = debounce(this.handleScroll, 10);
        this.handleWidthChange = debounce(this.handleWidthChange, 10);
    }

    state = {
        left: 0,
        nextActive: true,
        previousActive: false,
        width: 0,
    };

    componentDidMount() {
        if (this.scroller) {
            this.scroller.addEventListener('scroll', this.handleScroll);
            window.addEventListener('resize', this.handleWidthChange);
            window.addEventListener('orientationchange', this.handleWidthChange);
            this.handleScroll();
            this.handleWidthChange();
        }
    }

    shouldComponentUpdate = (nextProps: Props, nextState: State) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.state.width !== prevState.width || !isEqual(this.props.children, prevProps.children)) {
            this.handleScroll();
        }
    }

    componentWillUnmount() {
        if (this.scroller) {
            this.scroller.removeEventListener('scroll', this.handleScroll);
        }

        window.removeEventListener('resize', this.handleWidthChange);
        window.removeEventListener('orientationchange', this.handleWidthChange);
    }

    handleWidthChange = () => {
        if (this.state.width !== window.innerWidth) {
            this.setState({ width: window.innerWidth });
        }
    };

    handleScroll = () => {
        if (this.scroller) {
            this.setState({ left: this.scroller.scrollLeft }, this.updateActiveButtons);
        }
    };

    makeBreakPoints = () => {
        const points = [];
        let total = 0;
        const children = [].slice.call(this.container.children);
        const { marginBetweenCards } = this.props;
        children.forEach((child, index) => {
            if (index < children.length - 1) {
                total = child.offsetWidth + marginBetweenCards + total;
                points.push(total);
            } else {
                total = child.offsetWidth + total;
                points.push(total);
            }
        });

        return points;
    };

    updateActiveButtons = () => {
        const { left, nextActive, previousActive } = this.state;
        const newState: any = {};
        if (left <= 10) {
            newState.previousActive = false;
        } else if (!previousActive) {
            newState.previousActive = true;
        }
        const { width: scrollerWidth } = this.scroller.getBoundingClientRect();
        const containerWidth = this.getContainerWidth();

        if (left >= containerWidth - scrollerWidth - 10) {
            newState.nextActive = false;
        } else if (!nextActive) {
            newState.nextActive = true;
        }

        this.setState(newState);
    };

    scroller: any;
    container: any;

    getContainerWidth = () => {
        const { marginBetweenCards } = this.props;
        return (
            reduce(
                this.container.children,
                (total, card) => {
                    return total + card.offsetWidth;
                },
                0
            ) +
            (this.container.children.length - 1) * marginBetweenCards
        ); // this last bit is for the margins
    };

    next = () => {
        const points = this.makeBreakPoints();
        const { width: scrollerWidth } = this.scroller.getBoundingClientRect();

        let index = 0;
        for (let i = 0; i < points.length; i++) {
            if (points[i] > this.state.left + scrollerWidth) {
                index = i - 1;
                break;
            }
        }
        let left = points[index];

        if (left - 20 < this.state.left && this.state.left < left + 20) {
            left = points[index + 1];
        }

        this.setState({ left }, () => {
            this.updateActiveButtons();
            this.sideScroll(left, 0.5);
        });
    };

    previous = () => {
        const points = this.makeBreakPoints();
        const { width: scrollerWidth } = this.scroller.getBoundingClientRect();

        let left;

        if (this.state.left - scrollerWidth < 0) {
            left = 0;
        } else {
            let index = 0;
            for (let i = points.length - 1; i >= 0; i--) {
                if (points[i] < this.state.left - scrollerWidth) {
                    index = i + 1;
                    break;
                }
            }
            left = points[index];
            if (left - 20 < this.state.left && this.state.left < left + 20) {
                left = points[index - 1];
            }
        }

        this.setState({ left }, () => {
            this.updateActiveButtons();
            this.sideScroll(left, 0.5);
        });
    };

    sideScroll = (rangeInPixels: number, speed: number | null | undefined) => {
        const sequenceObj: any = {};
        const seconds = speed ? speed : 0.5;
        const startingScrollPosition = this.scroller.scrollLeft;
        const distance = rangeInPixels - startingScrollPosition;

        sequenceObj.progress = (percentage) => {
            let left = startingScrollPosition + easeQuadInOut(percentage) * distance;
            left = distance < 0 && left < 25 ? 0 : left;
            this.scroller.scrollLeft = left;
        };

        animate(sequenceObj, seconds);
    };

    render() {
        const { children, className, marginBetweenCards } = this.props;
        const { nextActive, previousActive } = this.state;
        const classes = classNames(styles['side-scroller'], className);
        return (
            <div className={classes}>
                {previousActive && (
                    <div className={styles.previous} onClick={this.previous}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </div>
                )}
                <div className={styles.scroller} ref={(ref) => (this.scroller = ref)}>
                    <div className={styles.container} ref={(ref) => (this.container = ref)}>
                        {Children.map(children, (child, index) => {
                            // @ts-ignore
                            const marginRight = index < children?.length - 1 ? `${marginBetweenCards}px` : '';
                            if (!child) {
                                return child;
                            }
                            return cloneElement(child, { style: { ...child?.props?.style, marginRight } });
                        })}
                    </div>
                </div>
                {nextActive && (
                    <div className={styles.next} onClick={this.next}>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                )}
            </div>
        );
    }
}

const easeQuadInOut = (t) => {
    return ((t *= 2) <= 1 ? t * t : --t * (2 - t) + 1) / 2;
};

const animate = (callbackObj, duration) => {
    const requestAnimationFrame =
        window.requestAnimationFrame ||
        window.webkitRequestAnimationFrame ||
        // @ts-ignore
        window.mozRequestAnimationFrame ||
        // @ts-ignore
        window.oRequestAnimationFrame ||
        // @ts-ignore
        window.msRequestAnimationFrame;

    let animationTime = 0;
    let percentage = 0;
    let startTime = 0;

    duration = duration * 1000 || 1000;

    const animation = (timestamp) => {
        if (startTime === 0) {
            startTime = timestamp;
        } else {
            animationTime = timestamp - startTime;
        }

        if (typeof callbackObj.start === 'function' && startTime === timestamp) {
            callbackObj.start();

            requestAnimationFrame(animation);
        } else if (animationTime < duration) {
            if (typeof callbackObj.progress === 'function') {
                percentage = animationTime / duration;
                callbackObj.progress(percentage);
            }

            requestAnimationFrame(animation);
        } else if (typeof callbackObj.done === 'function') {
            callbackObj.done();
        }
    };

    return requestAnimationFrame(animation);
};
